import {
  SMPSTelemetryItem,
  SMPSEventLogData,
  TransformedSMPSResponse,
} from './smpsApiTypes';
import {
  SMPSDevice,
  SMPSAlert,
  SMPSStats,
  StationStats,
  DeviceStatus,
  BatteryMode,
  PowerMode,
  SMPSAssetDetails,
  BatteryBank,
  LoadOutput,
  LVDConfig,
  SystemThresholds,
  EnvironmentData,
} from './smpsTypes';
import { VOLTAGE_THRESHOLDS } from './smpsConstants';
import dayjs from 'dayjs';
import { NewTelemetryItem, TransformedEventLogsResponse } from '../../Kavach-dashboard/utils/kavachApiTypes';

/**
 * Extract station code from device name
 * Device names follow patterns like:
 * - "SNS-SMPS-01-192.168.1.101" → Station: "SNS"
 * - "SKB-SMPS-02-192.168.2.101" → Station: "SKB"
 * - "HRS-192.168.11.24" → Station: "HRS"
 * - "HRS-SMPS-192.168.11.24" → Station: "HRS"
 * - "NCR_PRYJ_DER_SMPS_01" → Station: "DER" (3rd segment after zone/division)
 */
export const extractStationFromName = (deviceName: string): string => {
  if (!deviceName) return 'UNKNOWN';

  // Handle underscore-separated format: NCR_PRYJ_DER_SMPS_01
  if (deviceName.includes('_')) {
    const parts = deviceName.split('_');
    // Format: ZONE_DIVISION_STATION_TYPE_NUMBER
    // e.g., NCR_PRYJ_DER_SMPS_01 → Station: DER
    if (parts.length >= 3) {
      const station = parts[2].toUpperCase();
      // Check if it's a valid station code (3-4 letters, not SMPS/KAVACH)
      if (station && /^[A-Z]{2,4}$/.test(station) && station !== 'SMPS' && station !== 'KAVACH') {
        return station;
      }
    }
  }

  // Handle dash-separated format: HRS-SMPS-01-192.168.1.101
  const parts = deviceName.split('-');
  if (parts.length > 0) {
    const station = parts[0].toUpperCase();
    // Check if it's a valid station code (not an IP address or number)
    if (station && !/^\d+$/.test(station) && !station.includes('.')) {
      return station;
    }
  }

  return 'UNKNOWN';
};

/**
 * Parse the JSON data field from event log
 */
export const parseEventLogData = (dataString: string): SMPSEventLogData | null => {
  try {
    return JSON.parse(dataString) as SMPSEventLogData;
  } catch {
    return null;
  }
};

/**
 * Parse voltage value (assuming mV to V conversion)
 */
export const parseVoltage = (value: string | undefined): number => {
  if (!value) return 0;
  const parsed = parseFloat(value);
  // If value > 1000, assume it's in mV and convert to V
  return parsed > 1000 ? parsed / 1000 : parsed;
};

/**
 * Parse current value (assuming mA to A conversion)
 */
export const parseCurrent = (value: string | undefined): number => {
  if (!value) return 0;
  const parsed = parseFloat(value);
  // If value > 1000, assume it's in mA and convert to A
  return parsed > 1000 ? parsed / 1000 : parsed;
};

/**
 * Parse temperature value
 */
export const parseTemperature = (value: string | undefined): number => {
  if (!value) return 0;
  return parseFloat(value);
};

/**
 * Parse battery mode from API value
 */
export const parseBatteryMode = (value: string | undefined): BatteryMode => {
  if (!value) return 'UNKNOWN';
  const mode = parseInt(value, 10);
  switch (mode) {
    case 2:
    case 3:
      return 'FLOAT';
    case 4:
    case 5:
      return 'BOOST';
    case 6:
    case 7:
      return 'DISCHARGE';
    case 8:
    case 9:
      return 'TEST';
    default:
      return 'UNKNOWN';
  }
};

/**
 * Determine device status based on metrics
 */
export const determineDeviceStatus = (
  dcVoltage: number,
  alarmCount: number,
  lvdStatus: boolean,
  commStatus: string,
  rectifiersOnline: number,
  rectifiersTotal: number,
  createdAt: string
): DeviceStatus => {
  // if createdAt is older than (time difference between 2 latest packets for the asset + 5 mins) then show offline
  if (dayjs(createdAt).isBefore(dayjs().subtract(10, 'minute'))) return 'NA';
  // if (commStatus === 'OFFLINE' || dcVoltage === 0) return 'OFFLINE';
  if (alarmCount > 2 || lvdStatus) return 'CRITICAL';
  if (dcVoltage < VOLTAGE_THRESHOLDS.lowCritical || dcVoltage > VOLTAGE_THRESHOLDS.highCritical) return 'CRITICAL';
  if (alarmCount > 0) return 'WARNING';
  if (dcVoltage < VOLTAGE_THRESHOLDS.lowWarning || dcVoltage > VOLTAGE_THRESHOLDS.highWarning) return 'WARNING';
  if (rectifiersOnline < rectifiersTotal) return 'WARNING';
  return 'HEALTHY';
};

/**
 * Format ISO timestamp to display format in IST
 */
export const formatTimestamp = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    const istString = date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Kolkata',
    });
    return istString.replace(',', '');
  } catch {
    return isoString;
  }
};

/**
 * Get relative time string for alerts
 */
export const getRelativeTime = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} mins ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } catch {
    return 'Unknown';
  }
};

/**
 * Create asset details for a device using API data
 */
const createAssetDetails = (
  ipAddress: string,
  deviceName: string,
  results: SMPSEventLogData['results']
): SMPSAssetDetails => {
  // Extract manufacturer and model from API
  const manufacturer = results.manufacturer?.value?.trim() || 'Unknown';
  const model = results.model?.value?.trim() || 'DC Power System';

  // Extract firmware/software versions
  const softwareVersion = results.software_version?.value || results.internal_version?.value || 'N/A';
  const hardwareVersion = results.hardware_version?.value || 'N/A';

  // Extract capacity info
  const rectSlots = parseInt(results.rect_slot_count?.value || '2', 10);
  const floatVoltMv = parseInt(results.float_volt_mv?.value || '48000', 10);
  const floatVolt = Math.round(floatVoltMv / 1000);
  const capacity = `${floatVolt}V / ${rectSlots * 50}A (${rectSlots} Rectifiers)`;

  // Extract battery info for capacity
  const battCapacityMah = parseInt(results.batt_capacity_mah?.value || '0', 10);
  const battCapacityAh = battCapacityMah > 0 ? `${Math.round(battCapacityMah / 1000)}Ah` : 'N/A';

  return {
    make: manufacturer,
    model: model,
    serialNumber: deviceName,
    capacity: capacity,
    purchaseDate: 'N/A',
    installationDate: 'N/A',
    warrantyExpiry: 'N/A',
    codalLife: '15 Years',
    remainingLife: 'N/A',
    installationAge: 'N/A',
    warrantyStatus: 'Active',
    ipAddress,
    macAddress: 'N/A',
    firmwareVersion: `SW: ${softwareVersion}, HW: ${hardwareVersion}`,
    latitude: 'N/A',
    longitude: 'N/A',
    assetPosition: results.site_name?.value?.trim() || 'N/A',
    installationTeam: 'N/A',
    supervisor: 'N/A',
    lastMaintenance: 'N/A',
    nextMaintenanceDue: 'N/A',
  };
};

/**
 * Get value from results supporting both labeled and raw API format
 * Falls back to alternate field names if primary not found
 */
const getResultValue = (
  results: SMPSEventLogData['results'],
  primaryKey: string,
  ...alternateKeys: string[]
): string | undefined => {
  if (results[primaryKey]?.value) return results[primaryKey]?.value;
  for (const key of alternateKeys) {
    if (results[key]?.value) return results[key]?.value;
  }
  return undefined;
};

/**
 * Count online rectifiers from individual status fields
 */
const countOnlineRectifiers = (results: SMPSEventLogData['results']): number => {
  let count = 0;
  const rectStatuses = [
    results.Rect_1_Sts?.value,
    results.Rect_2_Sts?.value,
    results.Rect_3_Sts?.value,
    results.Rect_4_Sts?.value,
  ];
  rectStatuses.forEach((status) => {
    // Status '1' or '2' typically means online/working
    if (status === '1' || status === '2') count++;
  });
  return count;
};

/**
 * Calculate total rectifier current from individual currents
 */
const calculateTotalRectifierCurrent = (results: SMPSEventLogData['results']): number => {
  const currents = [
    parseFloat(results.Rect_1_Cur?.value || '0'),
    parseFloat(results.Rect_2_Cur?.value || '0'),
    parseFloat(results.Rect_3_Cur?.value || '0'),
    parseFloat(results.Rect_4_Cur?.value || '0'),
  ];
  return currents.reduce((sum, cur) => sum + cur, 0);
};

/**
 * Count active alarms from individual alarm fields
 */
const countActiveAlarms = (results: SMPSEventLogData['results']): number => {
  let count = 0;
  const alarmFields = [
    results.AC_Fail?.value,
    results.Rect_Fail?.value,
    results.Batt_Low?.value,
    results.LVD_Activated?.value,
    results.High_DC_Vol?.value,
    results.Low_DC_Vol?.value,
  ];
  alarmFields.forEach((val) => {
    if (val === '1' || val === 'true') count++;
  });
  return count;
};

/**
 * Parse battery mode from MIB psStatusBatteryMode value
 * Based on MPS-POWER-MIB: 6=Float, 8=Boost, 11=Discharge, 12=Test
 */
const parseMIBBatteryMode = (modeValue: string | undefined): BatteryMode => {
  if (!modeValue) return 'UNKNOWN';
  const mode = parseInt(modeValue, 10);
  switch (mode) {
    case 6: return 'FLOAT';       // mFloatCharging
    case 8: return 'BOOST';       // mBoostCharging
    case 11: return 'DISCHARGE';  // mDischarging
    case 12: return 'TEST';       // mTest
    case 2: return 'FLOAT';       // mStartPre-limit
    case 5: return 'FLOAT';       // mFloatPre-limit
    case 4: return 'BOOST';       // mBoostPre-limit
    case 7: return 'FLOAT';       // mTemp-Comp
    default: return 'UNKNOWN';
  }
};

/**
 * Parse temperature from MIB format (stored as 0.001°C)
 * e.g., 28000 = 28°C
 */
const parseMIBTemperature = (tempValue: string | undefined): number => {
  if (!tempValue) return 0;
  const temp = parseFloat(tempValue);
  // If value is > 1000, it's in 0.001°C format
  if (temp > 1000) return temp / 1000;
  // If value is > 100, it's in 0.1°C format
  if (temp > 100) return temp / 10;
  // Otherwise assume it's already in °C
  return temp;
};

/**
 * Parse voltage from MIB format (stored as mV)
 * e.g., 53710 = 53.71V
 */
export const parseMIBVoltage = (voltValue: string | undefined): number => {
  if (!voltValue) return 0;
  const volt = parseFloat(voltValue);
  // If value is > 1000, it's in mV format
  if (volt > 1000) return volt / 1000;
  // If value is > 100, it's likely in V*10 format
  if (volt > 100) return volt / 10;
  // Otherwise assume it's already in V
  return volt;
};

/**
 * Parse current from MIB format (stored as mA)
 * e.g., 45500 = 45.5A
 */
export const parseMIBCurrent = (currValue: string | undefined): number => {
  if (!currValue) return 0;
  const curr = parseFloat(currValue);
  // API field 'system_current_ma' is ALWAYS in mA format
  // e.g., 2367 = 2.367A, 17700 = 17.7A
  // So we always divide by 1000 to convert mA to A
  if (curr > 100) return curr / 1000;
  // For very small values, assume already in A
  return curr;
};

/**
 * Transform a single EventLog to SMPSDevice
 * Supports both labeled format and actual API field names from MEGMEET MC2600
 * API returns field names like: system_voltage_mv, battery_mode, ac_phase_a_volt_mv, etc.
 */
// export const transformEventLogToDevice = (
//   eventLog: SMPSEventLogItem,
//   parsedData: SMPSEventLogData,
//   timeDiffMs?: number
// ): SMPSDevice => {
//   const { results } = parsedData;

//   // DC Voltage - API: system_voltage_mv (in mV, e.g., 51503 = 51.503V)
//   const dcVoltageRaw = getResultValue(results, 'system_voltage_mv', 'DC_VOLTAGE', 'systemVoltage', 'DC_Output_Vol', 'system_voltage');
//   const dcVoltage = parseMIBVoltage(dcVoltageRaw);

//   // Load Current - API: system_current_ma (in mA, e.g., 2104 = 2.104A)
//   const loadCurrentRaw = getResultValue(results, 'system_current_ma', 'LOAD_CURRENT', 'systemCurrent', 'Load_Cur', 'system_current');
//   const loadCurrent = parseMIBCurrent(loadCurrentRaw);

//   // Battery Voltage - API: float_volt_mv or boost_volt_mv (in mV)
//   const batteryVoltageRaw = getResultValue(results, 'float_volt_mv', 'boost_volt_mv', 'BATTERY_VOLTAGE', 'floatVolt', 'boostVolt', 'BatteryFloat_Vol');
//   const batteryVoltage = parseMIBVoltage(batteryVoltageRaw);

//   // Battery Current - API: batt_total_current_ma or batt1_current_ma (in mA)
//   const batteryCurrentRaw = getResultValue(results, 'batt_total_current_ma', 'batt1_current_ma', 'BATTERY_CURRENT', 'psBatteryCurrent', 'psBatteryCurrent1', 'Batt_Chrg_Cur_Lim');
//   const batteryCurrent = parseMIBCurrent(batteryCurrentRaw);

//   // Battery Capacity - API: batt1_soc or batt2_soc
//   const batteryCapacityRaw = getResultValue(results, 'batt1_soc', 'batt2_soc', 'BATTERY_CAPACITY', 'psBatteryCapacity1', 'psBatteryCapacity2', 'Batt_Cap');
//   let batteryCapacity = 100;
//   if (batteryCapacityRaw) {
//     const capVal = parseFloat(batteryCapacityRaw);
//     // If value > 1000, it's in 0.001% format
//     batteryCapacity = capVal > 1000 ? Math.round(capVal / 1000) : Math.round(capVal);
//     // If still 0, assume 100% (full charge) for float mode
//     if (batteryCapacity === 0) batteryCapacity = 100;
//   }

//   // Battery Mode - API: battery_mode (6=Float, 8=Boost, 11=Discharge, 12=Test)
//   const batteryModeRaw = getResultValue(results, 'battery_mode', 'BATTERY_MODE', 'psStatusBatteryMode');
//   const batteryMode = batteryModeRaw ? parseMIBBatteryMode(batteryModeRaw) : 'UNKNOWN';

//   // Battery Temperature - API: batt_temp1_001c (in 0.001°C)
//   const batteryTempRaw = getResultValue(results, 'batt_temp1_001c', 'batt_temp2_001c', 'BATTERY_TEMP', 'psTemperature1', 'psTemperature2', 'Batt_Temp');
//   const batteryTemp = parseMIBTemperature(batteryTempRaw);

//   // AC Voltages - API: ac_phase_a_volt_mv, ac_phase_b_volt_mv, ac_phase_c_volt_mv (in mV)
//   const acVoltageRRaw = getResultValue(results, 'ac_phase_a_volt_mv', 'AC_VOLTAGE_R', 'psInputLineAVoltage', 'AC_Vol_1');
//   const acVoltageYRaw = getResultValue(results, 'ac_phase_b_volt_mv', 'AC_VOLTAGE_Y', 'psInputLineBVoltage', 'AC_Vol_2');
//   const acVoltageBRaw = getResultValue(results, 'ac_phase_c_volt_mv', 'AC_VOLTAGE_B', 'psInputLineCVoltage', 'AC_Vol_3');
//   const acVoltageR = parseMIBVoltage(acVoltageRRaw);
//   const acVoltageY = parseMIBVoltage(acVoltageYRaw);
//   const acVoltageB = parseMIBVoltage(acVoltageBRaw);

//   // AC Frequency - API: ac_frequency_01hz (in 0.1Hz, e.g., 497 = 49.7Hz)
//   const acFreqRaw = getResultValue(results, 'ac_frequency_01hz', 'AC_FREQUENCY', 'psInputFrequency', 'AC_Freq');
//   let acFrequency = 0;
//   if (acFreqRaw) {
//     const freqVal = parseFloat(acFreqRaw);
//     // ac_frequency_01hz is in 0.1Hz format (497 = 49.7Hz)
//     acFrequency = freqVal > 100 ? freqVal / 10 : freqVal;
//   }

//   // Rectifiers - API: rect_slot_count (number of rectifier slots), rect_total_power_w
//   let rectifierTotal = parseInt(getResultValue(results, 'rect_slot_count', 'RECTIFIER_TOTAL', 'rectNum') || '0', 10);
//   let rectifierOnline = parseInt(getResultValue(results, 'RECTIFIER_ONLINE') || '0', 10);
//   let rectifierCurrent = parseCurrent(getResultValue(results, 'RECTIFIER_CURRENT', 'rectIout'));
//   const rectifierPower = parseInt(results.rect_total_power_w?.value || '0', 10);

//   // If labeled format not available, calculate from raw API or default
//   if (rectifierTotal === 0) {
//     if (results.Rect_1_Sts || results.Rect_2_Sts || results.Rect_3_Sts || results.Rect_4_Sts) {
//       rectifierTotal = 4;
//       rectifierOnline = countOnlineRectifiers(results);
//       rectifierCurrent = calculateTotalRectifierCurrent(results);
//     } else if (results.onoffStatus || results.rect_slot_count) {
//       rectifierTotal = parseInt(results.rect_slot_count?.value || '4', 10);
//       rectifierOnline = dcVoltage > 0 ? rectifierTotal : 0;
//     } else {
//       // Default to 2 rectifiers (from API response: rect_slot_count = 2)
//       rectifierTotal = 2;
//       rectifierOnline = dcVoltage > 0 ? 2 : 0;
//     }
//   }

//   // Solar - API: solar_slot_count, solar_total_power_w
//   const solarSlots = parseInt(results.solar_slot_count?.value || '0', 10);
//   const solarPower = parseInt(results.solar_total_power_w?.value || '0', 10);

//   // Ambient Temperature - API: ext_env_temp1_01c (in 0.1°C) or batt_temp4_001c
//   const ambientTempRaw = getResultValue(results, 'ext_env_temp1_01c', 'ext_env_temp2_01c', 'batt_temp4_001c', 'AMBIENT_TEMP', 'psTemperature4', 'psTemperature3', 'Ambient_Temp');
//   const ambientTemp = parseMIBTemperature(ambientTempRaw);

//   // ===== NEW FIELDS EXTRACTION =====

//   // Float and Boost Voltage - API: float_volt_mv, boost_volt_mv
//   const floatVoltage = parseMIBVoltage(results.float_volt_mv?.value);
//   const boostVoltage = parseMIBVoltage(results.boost_volt_mv?.value);

//   // Power Mode - API: power_mode
//   const powerModeRaw = results.power_mode?.value || '';
//   let powerMode: PowerMode = 'UNKNOWN';
//   if (powerModeRaw === '1' || powerModeRaw.toLowerCase().includes('ac')) powerMode = 'AC';
//   else if (powerModeRaw === '2' || powerModeRaw.toLowerCase().includes('batt')) powerMode = 'BATTERY';
//   else if (powerModeRaw === '3' || powerModeRaw.toLowerCase().includes('solar')) powerMode = 'SOLAR';
//   else if (powerModeRaw === '4' || powerModeRaw.toLowerCase().includes('hybrid')) powerMode = 'HYBRID';

//   // Battery capacity in Ah - API: batt_capacity_mah
//   const battCapacityMah = parseInt(results.batt_capacity_mah?.value || '0', 10);
//   const battCapacityAh = battCapacityMah > 0 ? Math.round(battCapacityMah / 1000) : 0;

//   // Battery count and BMS count - API: batt_count, bms_count
//   const battCount = parseInt(results.batt_count?.value || '1', 10);
//   const bmsCount = parseInt(results.bms_count?.value || '0', 10);

//   // Individual battery banks - API: batt1_volt_mv, batt1_current_ma, batt1_soc, batt1_temp_001c, etc.
//   const batteryBanks: BatteryBank[] = [];
//   for (let i = 1; i <= 2; i++) {
//     const voltKey = `batt${i}_volt_mv`;
//     const currKey = `batt${i}_current_ma`;
//     const socKey = `batt${i}_soc`;
//     const tempKey = `batt${i}_temp_001c`;
//     if (results[voltKey]?.value || results[socKey]?.value) {
//       // SOC might be in 0.001% format (e.g., 100000 = 100%)
//       const socRaw = parseFloat(results[socKey]?.value || '0');
//       const socVal = socRaw > 1000 ? Math.round(socRaw / 1000) : Math.round(socRaw);
//       batteryBanks.push({
//         voltage: parseMIBVoltage(results[voltKey]?.value),
//         current: parseMIBCurrent(results[currKey]?.value),
//         soc: socVal,
//         temperature: parseMIBTemperature(results[tempKey]?.value),
//       });
//     }
//   }

//   // AC Input Type - API: ac_input_type
//   const acInputType = results.ac_input_type?.value || '3-Phase';

//   // Rectifier current limit - API: rect_config_curr_limit_ma
//   const rectCurrentLimit = parseMIBCurrent(results.rect_config_curr_limit_ma?.value);

//   // Individual load outputs - API: load1_current_ma, load1_today_energy_wh, load1_total_energy_wh, etc.
//   const loads: LoadOutput[] = [];
//   for (let i = 1; i <= 4; i++) {
//     const currKey = `load${i}_current_ma`;
//     const todayKey = `load${i}_today_energy_wh`;
//     const totalKey = `load${i}_total_energy_wh`;
//     loads.push({
//       current: parseMIBCurrent(results[currKey]?.value),
//       todayEnergy: parseInt(results[todayKey]?.value || '0', 10),
//       totalEnergy: parseInt(results[totalKey]?.value || '0', 10),
//     });
//   }

//   // Battery LVD - API: blvd_enable, blvd_volt
//   const batteryLvd: LVDConfig = {
//     enabled: results.blvd_enable?.value === '0', // 0=Enable
//     voltage: parseMIBVoltage(results.blvd_volt?.value),
//   };

//   // Load LVDs - API: llvd1_enable, llvd1_volt, etc.
//   const loadLvd: LVDConfig[] = [];
//   for (let i = 1; i <= 4; i++) {
//     const enableKey = `llvd${i}_enable`;
//     const voltKey = `llvd${i}_volt`;
//     loadLvd.push({
//       enabled: results[enableKey]?.value === '0', // 0=Enable
//       voltage: parseMIBVoltage(results[voltKey]?.value),
//     });
//   }

//   // System Thresholds - API: dc_volt_high_mv, dc_volt_low_mv, ac_over_volt_v, etc.
//   // Note: dc_volt_high_mv/dc_volt_low_mv are in mV (millivolts)
//   // e.g., 56000 = 56V, 42000 = 42V
//   const dcVoltHigh = parseMIBVoltage(results.dc_volt_high_mv?.value);
//   const dcVoltLow = parseMIBVoltage(results.dc_volt_low_mv?.value);

//   // Battery temperature thresholds - API: batt1_over_temp, batt1_high_temp, batt1_low_temp
//   // These are in 0.001°C format (e.g., 55000 = 55°C)
//   const battOverTempRaw = parseFloat(results.batt1_over_temp?.value || results.batt_over_temp?.value || '0');
//   const battHighTempRaw = parseFloat(results.batt1_high_temp?.value || results.batt_high_temp?.value || '0');
//   const battLowTempRaw = parseFloat(results.batt1_low_temp?.value || results.batt_low_temp?.value || '0');

//   // AC voltage thresholds - API returns in 0.1V format (e.g., 2800 = 280V)
//   const acOverVoltRaw = parseFloat(results.ac_over_volt_v?.value || '0');
//   const acUnderVoltRaw = parseFloat(results.ac_under_volt_v?.value || '0');
//   // Convert from 0.1V format to V (e.g., 2800 → 280V, 1800 → 180V)
//   const acOverVoltage = acOverVoltRaw > 1000 ? acOverVoltRaw / 10 : acOverVoltRaw;
//   const acUnderVoltage = acUnderVoltRaw > 1000 ? acUnderVoltRaw / 10 : acUnderVoltRaw;

//   const thresholds: SystemThresholds = {
//     dcVoltageHigh: parseFloat(dcVoltHigh.toFixed(1)),
//     dcVoltageLow: parseFloat(dcVoltLow.toFixed(1)),
//     acOverVoltage: parseFloat(acOverVoltage.toFixed(1)),
//     acUnderVoltage: parseFloat(acUnderVoltage.toFixed(1)),
//     battOverTemp: battOverTempRaw > 1000 ? battOverTempRaw / 1000 : battOverTempRaw,
//     battHighTemp: battHighTempRaw > 1000 ? battHighTempRaw / 1000 : battHighTempRaw,
//     battLowTemp: battLowTempRaw > 1000 ? battLowTempRaw / 1000 : battLowTempRaw,
//   };

//   // Additional battery temperatures - API: batt_temp2_001c, batt_temp3_001c, batt_temp4_001c
//   const batteryTemp2 = parseMIBTemperature(results.batt_temp2_001c?.value);
//   const batteryTemp3 = parseMIBTemperature(results.batt_temp3_001c?.value);
//   const batteryTemp4 = parseMIBTemperature(results.batt_temp4_001c?.value);

//   // Environment sensors - API: ext_env_temp1_01c, ext_env_temp2_01c, env_humidity_001rh
//   const envTemp1Raw = results.ext_env_temp1_01c?.value;
//   const envTemp2Raw = results.ext_env_temp2_01c?.value;
//   const humidityRaw = results.env_humidity_001rh?.value;
//   const environment: EnvironmentData = {
//     temperature1: envTemp1Raw ? parseFloat(envTemp1Raw) / 10 : 0, // 0.1°C format
//     temperature2: envTemp2Raw ? parseFloat(envTemp2Raw) / 10 : 0,
//     humidity: humidityRaw ? parseFloat(humidityRaw) / 1000 : 0, // 0.001%RH format
//   };

//   // Buzzer status - API: buzzer (0=Off, 1=On)
//   const buzzerEnabled = results.buzzer?.value === '1';

//   // System info - API: system_time, site_name, power_mode, system_status
//   const systemTime = results.system_time?.value || '';
//   const siteName = results.site_name?.value || '';
//   const systemStatusNum = parseInt(results.system_status?.value || '1', 10);

//   // System Status - API: system_status (1=unknown, 2=normal, 3=warning, 4=minor, 5=major)
//   const systemStatusRaw = getResultValue(results, 'system_status', 'SYSTEM_STATUS', 'systemStatus');
//   let alarmCount = parseInt(getResultValue(results, 'ALARM_COUNT') || '0', 10);
//   if (alarmCount === 0 && systemStatusRaw) {
//     const sysStatus = parseInt(systemStatusRaw, 10);
//     if (sysStatus >= 3) alarmCount = sysStatus - 2; // warning=1, minor=2, major=3
//   }
//   if (alarmCount === 0) {
//     alarmCount = countActiveAlarms(results);
//   }

//   // LVD Status - API: blvd_enable, llvd1_enable (0=Enable, 1=Disable)
//   const lvdStatusRaw = getResultValue(results, 'blvd_enable', 'llvd1_enable', 'LVD_STATUS', 'blvdEnable', 'llvd1Enable', 'LVD_Activated');
//   // blvd_enable: 0=Enable (LVD active), 1=Disable (LVD not active)
//   // For SMPS, '0' means LVD protection is enabled, not that LVD is triggered
//   const lvdStatus = lvdStatusRaw === '1' || lvdStatusRaw === 'true'; // LVD triggered if disabled or true

//   // Communication Status - API: comm_status (1=unknown, 2=normal, 3=interrupt)
//   const commStatusRaw = getResultValue(results, 'comm_status', 'COMM_STATUS', 'psStatusCommunication');
//   let commStatus: string = parsedData.status === 'success' ? 'ONLINE' : 'OFFLINE';
//   if (commStatusRaw === '3') commStatus = 'OFFLINE';

//   // Determine AC status based on voltage readings
//   const acFailRaw = results.AC_Fail?.value;
//   const acStatus = acFailRaw === '1' ? 'FAIL' :
//     (acVoltageR > 50 && acVoltageY > 50 && acVoltageB > 50 ? 'NORMAL' : 'FAIL');

//   // Calculate efficiency (approximate based on power)
//   const rectPowerW = parseInt(results.rect_total_power_w?.value || '0', 10);
//   const dcPowerW = dcVoltage * loadCurrent;
//   const efficiency = rectPowerW > 0 && dcPowerW > 0 ?
//     Math.min(95, (dcPowerW / rectPowerW) * 100) :
//     (dcVoltage > 0 && loadCurrent > 0 ? Math.min(95, 85 + (dcVoltage / 48) * 10) : 0);

//   const status = determineDeviceStatus(
//     dcVoltage,
//     alarmCount,
//     lvdStatus,
//     commStatus,
//     rectifierOnline,
//     rectifierTotal,
//     eventLog.createdAt
//   );

//   const assetDetails = createAssetDetails(
//     parsedData.deviceIp,
//     parsedData.deviceName,
//     results
//   );

//   return {
//     id: eventLog.deviceId.toString(),
//     name: parsedData.deviceName,
//     station: extractStationFromName(parsedData.deviceName),
//     createdAt: eventLog.createdAt,
//     lastActivity: formatTimestamp(eventLog.createdAt),
//     dcVoltage: parseFloat(dcVoltage.toFixed(2)),
//     loadCurrent: parseFloat(loadCurrent.toFixed(2)),
//     efficiency: parseFloat(efficiency.toFixed(1)),
//     floatVoltage: parseFloat(floatVoltage.toFixed(2)),
//     boostVoltage: parseFloat(boostVoltage.toFixed(2)),
//     powerMode,
//     battery: {
//       voltage: parseFloat(batteryVoltage.toFixed(2)),
//       current: parseFloat(batteryCurrent.toFixed(2)),
//       capacity: batteryCapacity,
//       mode: batteryMode,
//       temperature: batteryTemp,
//       capacityAh: battCapacityAh,
//       count: battCount,
//       bmsCount: bmsCount,
//     },
//     batteryBanks,
//     acInput: {
//       phaseR: parseFloat(acVoltageR.toFixed(1)),
//       phaseY: parseFloat(acVoltageY.toFixed(1)),
//       phaseB: parseFloat(acVoltageB.toFixed(1)),
//       frequency: parseFloat(acFrequency.toFixed(1)),
//       status: acStatus,
//       inputType: acInputType,
//     },
//     rectifiers: {
//       total: rectifierTotal || 4,
//       online: rectifierOnline,
//       totalCurrent: parseFloat(rectifierCurrent.toFixed(2)),
//       totalPower: rectifierPower,
//       currentLimit: parseFloat(rectCurrentLimit.toFixed(2)),
//     },
//     solar: {
//       slots: solarSlots,
//       totalPower: solarPower,
//     },
//     loads,
//     lvd: {
//       batteryLvd,
//       loadLvd,
//     },
//     thresholds,
//     temperature: {
//       ambient: ambientTemp,
//       battery: batteryTemp,
//       battery2: batteryTemp2,
//       battery3: batteryTemp3,
//       battery4: batteryTemp4,
//     },
//     environment,
//     alarms: {
//       activeCount: alarmCount,
//       lvdStatus,
//       overVoltage: dcVoltage > VOLTAGE_THRESHOLDS.highWarning,
//       underVoltage: dcVoltage < VOLTAGE_THRESHOLDS.lowWarning && dcVoltage > 0,
//       buzzerEnabled,
//     },
//     systemInfo: {
//       systemTime,
//       siteName,
//       powerMode: powerModeRaw,
//       systemStatus: systemStatusNum,
//     },
//     communicationStatus: commStatus as 'ONLINE' | 'OFFLINE' | 'DEGRADED',
//     status,
//     timeDiffMs,
//     assetDetails,
//     rawResults: results,
//   };
// };
export const transformTelemetryToSMPSDevice = (
  telemetryItem: NewTelemetryItem,
): SMPSDevice => {
  const deviceId = telemetryItem.deviceId;
  const getDeviceName = (deviceName: string) => {
    if (deviceName) {
      const [_zone, _div, _stn, device] = deviceName.split("_");
      return device;
    }
    return "";
  }
  const rawDeviceName = getDeviceName(telemetryItem.deviceName);

  let telemetryData: any = {};
  // Extract data from dataLists
  if (telemetryItem?.dataLists?.length) {
    telemetryItem?.dataLists.forEach((item) => {
      let dynamicRow: any = {};

      Object.entries(item.data).map(([key, value]) => {
        if (dynamicRow[key]) {
          const oldTs = dynamicRow[key].ts;
          const newTs = value[0].ts;
          if (newTs > oldTs) {
            dynamicRow[key] = {
              ts: newTs,
              value: value[0].value
            };
          }
        } else {
          dynamicRow[key] = {
            ts: value[0].ts,
            value: value[0].value
          };
        }
      });

      telemetryData = { ...telemetryData, ...dynamicRow };
    });
  }
  telemetryData = Object.keys(telemetryData).reduce((acc: any, key) => {
    acc[key] = telemetryData[key].value;
    return acc;
  }, {});

  return parseSMPSDeviceData(telemetryData, telemetryItem);
};

export const parseSMPSDeviceData = (
  telemetryData: any,
  telemetryItem: NewTelemetryItem
): SMPSDevice => {
  const { results } = telemetryData;

  // DC Voltage - API: system_voltage_mv (in mV, e.g., 51503 = 51.503V)
  const dcVoltageRaw = getResultValue(results, 'system_voltage_mv', 'DC_VOLTAGE', 'systemVoltage', 'DC_Output_Vol', 'system_voltage');
  const dcVoltage = parseMIBVoltage(dcVoltageRaw);

  // Load Current - API: system_current_ma (in mA, e.g., 2104 = 2.104A)
  const loadCurrentRaw = getResultValue(results, 'system_current_ma', 'LOAD_CURRENT', 'systemCurrent', 'Load_Cur', 'system_current');
  const loadCurrent = parseMIBCurrent(loadCurrentRaw);

  // Battery Voltage - API: float_volt_mv or boost_volt_mv (in mV)
  const batteryVoltageRaw = getResultValue(results, 'float_volt_mv', 'boost_volt_mv', 'BATTERY_VOLTAGE', 'floatVolt', 'boostVolt', 'BatteryFloat_Vol');
  const batteryVoltage = parseMIBVoltage(batteryVoltageRaw);

  // Battery Current - API: batt_total_current_ma or batt1_current_ma (in mA)
  const batteryCurrentRaw = getResultValue(results, 'batt_total_current_ma', 'batt1_current_ma', 'BATTERY_CURRENT', 'psBatteryCurrent', 'psBatteryCurrent1', 'Batt_Chrg_Cur_Lim');
  const batteryCurrent = parseMIBCurrent(batteryCurrentRaw);

  // Battery Capacity - API: batt1_soc or batt2_soc
  const batteryCapacityRaw = getResultValue(results, 'batt1_soc', 'batt2_soc', 'BATTERY_CAPACITY', 'psBatteryCapacity1', 'psBatteryCapacity2', 'Batt_Cap');
  let batteryCapacity = 100;
  if (batteryCapacityRaw) {
    const capVal = parseFloat(batteryCapacityRaw);
    // If value > 1000, it's in 0.001% format
    batteryCapacity = capVal > 1000 ? Math.round(capVal / 1000) : Math.round(capVal);
    // If still 0, assume 100% (full charge) for float mode
    if (batteryCapacity === 0) batteryCapacity = 100;
  }

  // Battery Mode - API: battery_mode (6=Float, 8=Boost, 11=Discharge, 12=Test)
  const batteryModeRaw = getResultValue(results, 'battery_mode', 'BATTERY_MODE', 'psStatusBatteryMode');
  const batteryMode = batteryModeRaw ? parseMIBBatteryMode(batteryModeRaw) : 'UNKNOWN';

  // Battery Temperature - API: batt_temp1_001c (in 0.001°C)
  const batteryTempRaw = getResultValue(results, 'batt_temp1_001c', 'batt_temp2_001c', 'BATTERY_TEMP', 'psTemperature1', 'psTemperature2', 'Batt_Temp');
  const batteryTemp = parseMIBTemperature(batteryTempRaw);

  // AC Voltages - API: ac_phase_a_volt_mv, ac_phase_b_volt_mv, ac_phase_c_volt_mv (in mV)
  const acVoltageRRaw = getResultValue(results, 'ac_phase_a_volt_mv', 'AC_VOLTAGE_R', 'psInputLineAVoltage', 'AC_Vol_1');
  const acVoltageYRaw = getResultValue(results, 'ac_phase_b_volt_mv', 'AC_VOLTAGE_Y', 'psInputLineBVoltage', 'AC_Vol_2');
  const acVoltageBRaw = getResultValue(results, 'ac_phase_c_volt_mv', 'AC_VOLTAGE_B', 'psInputLineCVoltage', 'AC_Vol_3');
  const acVoltageR = parseMIBVoltage(acVoltageRRaw);
  const acVoltageY = parseMIBVoltage(acVoltageYRaw);
  const acVoltageB = parseMIBVoltage(acVoltageBRaw);

  // AC Frequency - API: ac_frequency_01hz (in 0.1Hz, e.g., 497 = 49.7Hz)
  const acFreqRaw = getResultValue(results, 'ac_frequency_01hz', 'AC_FREQUENCY', 'psInputFrequency', 'AC_Freq');
  let acFrequency = 0;
  if (acFreqRaw) {
    const freqVal = parseFloat(acFreqRaw);
    // ac_frequency_01hz is in 0.1Hz format (497 = 49.7Hz)
    acFrequency = freqVal > 100 ? freqVal / 10 : freqVal;
  }

  // Rectifiers - API: rect_slot_count (number of rectifier slots), rect_total_power_w
  let rectifierTotal = parseInt(getResultValue(results, 'rect_slot_count', 'RECTIFIER_TOTAL', 'rectNum') || '0', 10);
  let rectifierOnline = parseInt(getResultValue(results, 'RECTIFIER_ONLINE') || '0', 10);
  let rectifierCurrent = parseCurrent(getResultValue(results, 'RECTIFIER_CURRENT', 'rectIout'));
  const rectifierPower = parseInt(results.rect_total_power_w?.value || '0', 10);

  // If labeled format not available, calculate from raw API or default
  if (rectifierTotal === 0) {
    if (results.Rect_1_Sts || results.Rect_2_Sts || results.Rect_3_Sts || results.Rect_4_Sts) {
      rectifierTotal = 4;
      rectifierOnline = countOnlineRectifiers(results);
      rectifierCurrent = calculateTotalRectifierCurrent(results);
    } else if (results.onoffStatus || results.rect_slot_count) {
      rectifierTotal = parseInt(results.rect_slot_count?.value || '4', 10);
      rectifierOnline = dcVoltage > 0 ? rectifierTotal : 0;
    } else {
      // Default to 2 rectifiers (from API response: rect_slot_count = 2)
      rectifierTotal = 2;
      rectifierOnline = dcVoltage > 0 ? 2 : 0;
    }
  }

  // Solar - API: solar_slot_count, solar_total_power_w
  const solarSlots = parseInt(results.solar_slot_count?.value || '0', 10);
  const solarPower = parseInt(results.solar_total_power_w?.value || '0', 10);

  // Ambient Temperature - API: ext_env_temp1_01c (in 0.1°C) or batt_temp4_001c
  const ambientTempRaw = getResultValue(results, 'ext_env_temp1_01c', 'ext_env_temp2_01c', 'batt_temp4_001c', 'AMBIENT_TEMP', 'psTemperature4', 'psTemperature3', 'Ambient_Temp');
  const ambientTemp = parseMIBTemperature(ambientTempRaw);

  // ===== NEW FIELDS EXTRACTION =====

  // Float and Boost Voltage - API: float_volt_mv, boost_volt_mv
  const floatVoltage = parseMIBVoltage(results.float_volt_mv?.value);
  const boostVoltage = parseMIBVoltage(results.boost_volt_mv?.value);

  // Power Mode - API: power_mode
  const powerModeRaw = results.power_mode?.value || '';
  let powerMode: PowerMode = 'UNKNOWN';
  if (powerModeRaw === '1' || powerModeRaw.toLowerCase().includes('ac')) powerMode = 'AC';
  else if (powerModeRaw === '2' || powerModeRaw.toLowerCase().includes('batt')) powerMode = 'BATTERY';
  else if (powerModeRaw === '3' || powerModeRaw.toLowerCase().includes('solar')) powerMode = 'SOLAR';
  else if (powerModeRaw === '4' || powerModeRaw.toLowerCase().includes('hybrid')) powerMode = 'HYBRID';

  // Battery capacity in Ah - API: batt_capacity_mah
  const battCapacityMah = parseInt(results.batt_capacity_mah?.value || '0', 10);
  const battCapacityAh = battCapacityMah > 0 ? Math.round(battCapacityMah / 1000) : 0;

  // Battery count and BMS count - API: batt_count, bms_count
  const battCount = parseInt(results.batt_count?.value || '1', 10);
  const bmsCount = parseInt(results.bms_count?.value || '0', 10);

  // Individual battery banks - API: batt1_volt_mv, batt1_current_ma, batt1_soc, batt1_temp_001c, etc.
  const batteryBanks: BatteryBank[] = [];
  for (let i = 1; i <= 2; i++) {
    const voltKey = `batt${i}_volt_mv`;
    const currKey = `batt${i}_current_ma`;
    const socKey = `batt${i}_soc`;
    const tempKey = `batt${i}_temp_001c`;
    if (results[voltKey]?.value || results[socKey]?.value) {
      // SOC might be in 0.001% format (e.g., 100000 = 100%)
      const socRaw = parseFloat(results[socKey]?.value || '0');
      const socVal = socRaw > 1000 ? Math.round(socRaw / 1000) : Math.round(socRaw);
      batteryBanks.push({
        voltage: parseMIBVoltage(results[voltKey]?.value),
        current: parseMIBCurrent(results[currKey]?.value),
        soc: socVal,
        temperature: parseMIBTemperature(results[tempKey]?.value),
      });
    }
  }

  // AC Input Type - API: ac_input_type
  const acInputType = results.ac_input_type?.value || '3-Phase';

  // Rectifier current limit - API: rect_config_curr_limit_ma
  const rectCurrentLimit = parseMIBCurrent(results.rect_config_curr_limit_ma?.value);

  // Individual load outputs - API: load1_current_ma, load1_today_energy_wh, load1_total_energy_wh, etc.
  const loads: LoadOutput[] = [];
  for (let i = 1; i <= 4; i++) {
    const currKey = `load${i}_current_ma`;
    const todayKey = `load${i}_today_energy_wh`;
    const totalKey = `load${i}_total_energy_wh`;
    loads.push({
      current: parseMIBCurrent(results[currKey]?.value),
      todayEnergy: parseInt(results[todayKey]?.value || '0', 10),
      totalEnergy: parseInt(results[totalKey]?.value || '0', 10),
    });
  }

  // Battery LVD - API: blvd_enable, blvd_volt
  const batteryLvd: LVDConfig = {
    enabled: results.blvd_enable?.value === '0', // 0=Enable
    voltage: parseMIBVoltage(results.blvd_volt?.value),
  };

  // Load LVDs - API: llvd1_enable, llvd1_volt, etc.
  const loadLvd: LVDConfig[] = [];
  for (let i = 1; i <= 4; i++) {
    const enableKey = `llvd${i}_enable`;
    const voltKey = `llvd${i}_volt`;
    loadLvd.push({
      enabled: results[enableKey]?.value === '0', // 0=Enable
      voltage: parseMIBVoltage(results[voltKey]?.value),
    });
  }

  // System Thresholds - API: dc_volt_high_mv, dc_volt_low_mv, ac_over_volt_v, etc.
  // Note: dc_volt_high_mv/dc_volt_low_mv are in mV (millivolts)
  // e.g., 56000 = 56V, 42000 = 42V
  const dcVoltHigh = parseMIBVoltage(results.dc_volt_high_mv?.value);
  const dcVoltLow = parseMIBVoltage(results.dc_volt_low_mv?.value);

  // Battery temperature thresholds - API: batt1_over_temp, batt1_high_temp, batt1_low_temp
  // These are in 0.001°C format (e.g., 55000 = 55°C)
  const battOverTempRaw = parseFloat(results.batt1_over_temp?.value || results.batt_over_temp?.value || '0');
  const battHighTempRaw = parseFloat(results.batt1_high_temp?.value || results.batt_high_temp?.value || '0');
  const battLowTempRaw = parseFloat(results.batt1_low_temp?.value || results.batt_low_temp?.value || '0');

  // AC voltage thresholds - API returns in 0.1V format (e.g., 2800 = 280V)
  const acOverVoltRaw = parseFloat(results.ac_over_volt_v?.value || '0');
  const acUnderVoltRaw = parseFloat(results.ac_under_volt_v?.value || '0');
  // Convert from 0.1V format to V (e.g., 2800 → 280V, 1800 → 180V)
  const acOverVoltage = acOverVoltRaw > 1000 ? acOverVoltRaw / 10 : acOverVoltRaw;
  const acUnderVoltage = acUnderVoltRaw > 1000 ? acUnderVoltRaw / 10 : acUnderVoltRaw;

  const thresholds: SystemThresholds = {
    dcVoltageHigh: parseFloat(dcVoltHigh.toFixed(1)),
    dcVoltageLow: parseFloat(dcVoltLow.toFixed(1)),
    acOverVoltage: parseFloat(acOverVoltage.toFixed(1)),
    acUnderVoltage: parseFloat(acUnderVoltage.toFixed(1)),
    battOverTemp: battOverTempRaw > 1000 ? battOverTempRaw / 1000 : battOverTempRaw,
    battHighTemp: battHighTempRaw > 1000 ? battHighTempRaw / 1000 : battHighTempRaw,
    battLowTemp: battLowTempRaw > 1000 ? battLowTempRaw / 1000 : battLowTempRaw,
  };

  // Additional battery temperatures - API: batt_temp2_001c, batt_temp3_001c, batt_temp4_001c
  const batteryTemp2 = parseMIBTemperature(results.batt_temp2_001c?.value);
  const batteryTemp3 = parseMIBTemperature(results.batt_temp3_001c?.value);
  const batteryTemp4 = parseMIBTemperature(results.batt_temp4_001c?.value);

  // Environment sensors - API: ext_env_temp1_01c, ext_env_temp2_01c, env_humidity_001rh
  const envTemp1Raw = results.ext_env_temp1_01c?.value;
  const envTemp2Raw = results.ext_env_temp2_01c?.value;
  const humidityRaw = results.env_humidity_001rh?.value;
  const environment: EnvironmentData = {
    temperature1: envTemp1Raw ? parseFloat(envTemp1Raw) / 10 : 0, // 0.1°C format
    temperature2: envTemp2Raw ? parseFloat(envTemp2Raw) / 10 : 0,
    humidity: humidityRaw ? parseFloat(humidityRaw) / 1000 : 0, // 0.001%RH format
  };

  // Buzzer status - API: buzzer (0=Off, 1=On)
  const buzzerEnabled = results.buzzer?.value === '1';

  // System info - API: system_time, site_name, power_mode, system_status
  const systemTime = results.system_time?.value || '';
  const siteName = results.site_name?.value || '';
  const systemStatusNum = parseInt(results.system_status?.value || '1', 10);

  // System Status - API: system_status (1=unknown, 2=normal, 3=warning, 4=minor, 5=major)
  const systemStatusRaw = getResultValue(results, 'system_status', 'SYSTEM_STATUS', 'systemStatus');
  let alarmCount = parseInt(getResultValue(results, 'ALARM_COUNT') || '0', 10);
  if (alarmCount === 0 && systemStatusRaw) {
    const sysStatus = parseInt(systemStatusRaw, 10);
    if (sysStatus >= 3) alarmCount = sysStatus - 2; // warning=1, minor=2, major=3
  }
  if (alarmCount === 0) {
    alarmCount = countActiveAlarms(results);
  }

  // LVD Status - API: blvd_enable, llvd1_enable (0=Enable, 1=Disable)
  const lvdStatusRaw = getResultValue(results, 'blvd_enable', 'llvd1_enable', 'LVD_STATUS', 'blvdEnable', 'llvd1Enable', 'LVD_Activated');
  // blvd_enable: 0=Enable (LVD active), 1=Disable (LVD not active)
  // For SMPS, '0' means LVD protection is enabled, not that LVD is triggered
  const lvdStatus = lvdStatusRaw === '1' || lvdStatusRaw === 'true'; // LVD triggered if disabled or true

  // Communication Status - API: comm_status (1=unknown, 2=normal, 3=interrupt)
  const commStatusRaw = getResultValue(results, 'comm_status', 'COMM_STATUS', 'psStatusCommunication');
  let commStatus: string = results.status === 'success' ? 'ONLINE' : 'OFFLINE';
  if (commStatusRaw === '3') commStatus = 'OFFLINE';

  // Determine AC status based on voltage readings
  const acFailRaw = results.AC_Fail?.value;
  const acStatus = acFailRaw === '1' ? 'FAIL' :
    (acVoltageR > 50 && acVoltageY > 50 && acVoltageB > 50 ? 'NORMAL' : 'FAIL');

  // Calculate efficiency (approximate based on power)
  const rectPowerW = parseInt(results.rect_total_power_w?.value || '0', 10);
  const dcPowerW = dcVoltage * loadCurrent;
  const efficiency = rectPowerW > 0 && dcPowerW > 0 ?
    Math.min(95, (dcPowerW / rectPowerW) * 100) :
    (dcVoltage > 0 && loadCurrent > 0 ? Math.min(95, 85 + (dcVoltage / 48) * 10) : 0);
  const eventLogDate = new Date(telemetryItem.ts).toISOString()

  const status = determineDeviceStatus(
    dcVoltage,
    alarmCount,
    lvdStatus,
    commStatus,
    rectifierOnline,
    rectifierTotal,
    eventLogDate
  );

  const assetDetails = createAssetDetails(
    telemetryData.deviceIp,
    telemetryData.deviceName,
    telemetryData
  );

  return {
    id: telemetryItem.deviceId.toString(),
    name: telemetryData.deviceName,
    station: extractStationFromName(telemetryData.deviceName),
    createdAt: eventLogDate,
    lastActivity: formatTimestamp(eventLogDate),
    dcVoltage: parseFloat(dcVoltage.toFixed(2)),
    loadCurrent: parseFloat(loadCurrent.toFixed(2)),
    efficiency: parseFloat(efficiency.toFixed(1)),
    floatVoltage: parseFloat(floatVoltage.toFixed(2)),
    boostVoltage: parseFloat(boostVoltage.toFixed(2)),
    powerMode,
    battery: {
      voltage: parseFloat(batteryVoltage.toFixed(2)),
      current: parseFloat(batteryCurrent.toFixed(2)),
      capacity: batteryCapacity,
      mode: batteryMode,
      temperature: batteryTemp,
      capacityAh: battCapacityAh,
      count: battCount,
      bmsCount: bmsCount,
    },
    batteryBanks,
    acInput: {
      phaseR: parseFloat(acVoltageR.toFixed(1)),
      phaseY: parseFloat(acVoltageY.toFixed(1)),
      phaseB: parseFloat(acVoltageB.toFixed(1)),
      frequency: parseFloat(acFrequency.toFixed(1)),
      status: acStatus,
      inputType: acInputType,
    },
    rectifiers: {
      total: rectifierTotal || 4,
      online: rectifierOnline,
      totalCurrent: parseFloat(rectifierCurrent.toFixed(2)),
      totalPower: rectifierPower,
      currentLimit: parseFloat(rectCurrentLimit.toFixed(2)),
    },
    solar: {
      slots: solarSlots,
      totalPower: solarPower,
    },
    loads,
    lvd: {
      batteryLvd,
      loadLvd,
    },
    thresholds,
    temperature: {
      ambient: ambientTemp,
      battery: batteryTemp,
      battery2: batteryTemp2,
      battery3: batteryTemp3,
      battery4: batteryTemp4,
    },
    environment,
    alarms: {
      activeCount: alarmCount,
      lvdStatus,
      overVoltage: dcVoltage > VOLTAGE_THRESHOLDS.highWarning,
      underVoltage: dcVoltage < VOLTAGE_THRESHOLDS.lowWarning && dcVoltage > 0,
      buzzerEnabled,
    },
    systemInfo: {
      systemTime,
      siteName,
      powerMode: powerModeRaw,
      systemStatus: systemStatusNum,
    },
    communicationStatus: commStatus as 'ONLINE' | 'OFFLINE' | 'DEGRADED',
    status,
    assetDetails,
    rawResults: telemetryData,
  };
};

/**
 * Aggregate event logs to get latest status per device
 */
// export const aggregateDeviceData = (
//   eventLogs: SMPSTelemetryItem[]
// ): Map<number, { eventLog: SMPSTelemetryItem; parsedData: SMPSEventLogData; timeDiffMs?: number }> => {
//   const deviceMap = new Map<number, {
//     latest: SMPSTelemetryItem;
//     secondLatest?: SMPSTelemetryItem;
//     parsedData: SMPSEventLogData;
//   }>();

//   for (const eventLog of eventLogs) {
//     const parsedData = parseEventLogData(eventLog.data);
//     if (!parsedData) continue;

//     const existing = deviceMap.get(eventLog.deviceId);
//     if (!existing) {
//       deviceMap.set(eventLog.deviceId, { latest: eventLog, parsedData });
//     } else {
//       if (eventLog.id > existing.latest.id) {
//         // Current becomes second latest, eventLog becomes latest
//         deviceMap.set(eventLog.deviceId, {
//           latest: eventLog,
//           secondLatest: existing.latest,
//           parsedData
//         });
//       } else if (!existing.secondLatest || eventLog.id > existing.secondLatest.id) {
//         // eventLog is newer than current secondLatest but older than latest
//         existing.secondLatest = eventLog;
//       }
//     }
//   }

//   const result = new Map<number, { eventLog: SMPSTelemetryItem; parsedData: SMPSEventLogData; timeDiffMs?: number }>();
//   deviceMap.forEach((val, deviceId) => {
//     let timeDiffMs: number | undefined;
//     if (val.latest && val.secondLatest) {
//       timeDiffMs = Math.abs(
//         new Date(val.latest.createdAt).getTime() -
//         new Date(val.secondLatest.createdAt).getTime()
//       );
//     }
//     result.set(deviceId, {
//       eventLog: val.latest,
//       parsedData: val.parsedData,
//       timeDiffMs
//     });
//   });

//   return result;
// };

/**
 * Generate alerts from devices with issues
 */
export const generateAlertsFromDevices = (
  devices: SMPSDevice[]
): SMPSAlert[] => {
  const alerts: SMPSAlert[] = [];
  let alertId = 1;

  for (const device of devices) {
    // AC Failure
    if (device.acInput.status === 'FAIL' && device.communicationStatus === 'ONLINE') {
      alerts.push({
        id: (alertId++).toString(),
        type: 'critical',
        title: `AC Mains Failure - ${device.station} Station`,
        description: `${device.name} - Complete AC power failure`,
        timestamp: getRelativeTime(new Date().toISOString()),
        deviceId: device.id,
        alarmType: 'AC_FAIL',
      });
    }

    // Low Battery
    if (device.battery.capacity < 50 && device.battery.mode === 'DISCHARGE') {
      alerts.push({
        id: (alertId++).toString(),
        type: 'critical',
        title: `Low Battery - ${device.station} Station`,
        description: `${device.name} - Battery at ${device.battery.capacity}%`,
        timestamp: getRelativeTime(new Date().toISOString()),
        deviceId: device.id,
        alarmType: 'BATTERY_LOW',
      });
    }

    // LVD Active
    if (device.alarms.lvdStatus) {
      alerts.push({
        id: (alertId++).toString(),
        type: 'critical',
        title: `LVD Active - ${device.station} Station`,
        description: `${device.name} - Low Voltage Disconnect active`,
        timestamp: getRelativeTime(new Date().toISOString()),
        deviceId: device.id,
        alarmType: 'LVD_ACTIVE',
      });
    }

    // Communication Lost
    if (device.communicationStatus === 'OFFLINE') {
      alerts.push({
        id: (alertId++).toString(),
        type: 'critical',
        title: `Communication Lost - ${device.station} Station`,
        description: `${device.name} - No response from device`,
        timestamp: getRelativeTime(new Date().toISOString()),
        deviceId: device.id,
        alarmType: 'COMM_FAIL',
      });
    }

    // Rectifier Offline
    if (device.rectifiers.online < device.rectifiers.total && device.communicationStatus === 'ONLINE') {
      const offlineCount = device.rectifiers.total - device.rectifiers.online;
      alerts.push({
        id: (alertId++).toString(),
        type: offlineCount > 1 ? 'critical' : 'warning',
        title: `Rectifier Offline - ${device.station} Station`,
        description: `${device.name} - ${offlineCount} of ${device.rectifiers.total} rectifiers offline`,
        timestamp: getRelativeTime(new Date().toISOString()),
        deviceId: device.id,
        alarmType: 'RECTIFIER_FAIL',
      });
    }

    // High Temperature
    if (device.temperature.ambient > 40) {
      alerts.push({
        id: (alertId++).toString(),
        type: 'warning',
        title: `High Temperature - ${device.station} Station`,
        description: `${device.name} - Ambient temperature ${device.temperature.ambient}°C`,
        timestamp: getRelativeTime(new Date().toISOString()),
        deviceId: device.id,
        alarmType: 'HIGH_TEMP',
      });
    }

    // DC Voltage Issues
    if (device.alarms.overVoltage) {
      alerts.push({
        id: (alertId++).toString(),
        type: 'warning',
        title: `High DC Voltage - ${device.station} Station`,
        description: `${device.name} - DC voltage at ${device.dcVoltage}V`,
        timestamp: getRelativeTime(new Date().toISOString()),
        deviceId: device.id,
        alarmType: 'DC_OVERVOLTAGE',
      });
    }

    if (device.alarms.underVoltage) {
      alerts.push({
        id: (alertId++).toString(),
        type: 'critical',
        title: `Low DC Voltage - ${device.station} Station`,
        description: `${device.name} - DC voltage at ${device.dcVoltage}V`,
        timestamp: getRelativeTime(new Date().toISOString()),
        deviceId: device.id,
        alarmType: 'DC_UNDERVOLTAGE',
      });
    }
  }

  // Sort by type (critical first) and limit
  return alerts
    .sort((a, b) => {
      if (a.type === 'critical' && b.type !== 'critical') return -1;
      if (a.type !== 'critical' && b.type === 'critical') return 1;
      return 0;
    })
    .slice(0, 10);
};

/**
 * Calculate statistics from devices
 */
export const calculateStatsFromDevices = (
  devices: SMPSDevice[]
): SMPSStats => {
  let healthy = 0;
  let warning = 0;
  let critical = 0;
  let offline = 0;
  let totalEfficiency = 0;
  let totalLoad = 0;
  let onlineCount = 0;

  devices.forEach((device) => {
    switch (device.status) {
      case 'HEALTHY':
        healthy++;
        break;
      case 'WARNING':
        warning++;
        break;
      case 'CRITICAL':
        critical++;
        break;
      case 'OFFLINE':
        offline++;
        break;
    }

    if (device.communicationStatus === 'ONLINE') {
      totalEfficiency += device.efficiency;
      totalLoad += device.loadCurrent;
      onlineCount++;
    }
  });

  return {
    totalDevices: devices.length,
    healthy,
    warning,
    critical,
    offline,
    avgEfficiency: onlineCount > 0 ? parseFloat((totalEfficiency / onlineCount).toFixed(1)) : 0,
    totalLoad: parseFloat(totalLoad.toFixed(1)),
  };
};

/**
 * Calculate station-wise statistics
 */
export const calculateStationStats = (
  devices: SMPSDevice[]
): StationStats[] => {
  const stationMap = new Map<string, StationStats>();

  devices.forEach((device) => {
    const station = device.station;
    const existing = stationMap.get(station) || {
      station,
      totalDevices: 0,
      avgVoltage: 0,
      totalCurrent: 0,
      healthyCount: 0,
      warningCount: 0,
      criticalCount: 0,
    };

    existing.totalDevices++;
    existing.totalCurrent += device.loadCurrent;

    if (device.communicationStatus === 'ONLINE') {
      existing.avgVoltage = (existing.avgVoltage * (existing.totalDevices - 1) + device.dcVoltage) / existing.totalDevices;
    }

    switch (device.status) {
      case 'HEALTHY':
        existing.healthyCount++;
        break;
      case 'WARNING':
        existing.warningCount++;
        break;
      case 'CRITICAL':
      case 'OFFLINE':
        existing.criticalCount++;
        break;
    }

    stationMap.set(station, existing);
  });

  return Array.from(stationMap.values()).map((stats) => ({
    ...stats,
    avgVoltage: parseFloat(stats.avgVoltage.toFixed(2)),
    totalCurrent: parseFloat(stats.totalCurrent.toFixed(2)),
  }));
};

/**
 * Main transformation function
 * Transforms raw API event logs to SMPS dashboard data
 */
export const transformTelemetryToSMPSData = (
  items: NewTelemetryItem[],
  totalElements: number,
  totalPages: number,
  currentPage: number
): TransformedSMPSResponse => {
  // Aggregate to get latest status per device
  // const deviceMap = aggregateDeviceData(items);

  // // Transform to devices
  // const devices: SMPSDevice[] = [];
  // deviceMap.forEach(({ eventLog, parsedData, timeDiffMs }) => {
  //   devices.push(transformTelemetryToSMPSDevice(eventLog, parsedData, timeDiffMs));
  // });

  const devices = items.map(item => {
    return transformTelemetryToSMPSDevice(item)
  })

  // Sort devices by station and name
  devices.sort((a, b) => {
    if (a.station !== b.station) {
      return a.station.localeCompare(b.station);
    }
    return a.name.localeCompare(b.name);
  });


  const alerts = generateAlertsFromDevices(devices);
  const stats = calculateStatsFromDevices(devices);
  const stationStats = calculateStationStats(devices);

  // Extract unique stations dynamically
  const uniqueStations = [...new Set(devices.map((d) => d.station))].sort();
  const stations = ['ALL', ...uniqueStations];

  return {
    devices,
    alerts,
    stats,
    stationStats,
    stations,
    totalElements,
    totalPages,
    currentPage,
  };
};
// export const transformSMPSEventLogs = (
//   eventLogs: SMPSEventLogItem[],
//   totalElements: number,
//   totalPages: number,
//   currentPage: number
// ): TransformedSMPSResponse => {
//   // Aggregate to get latest status per device
//   const deviceMap = aggregateDeviceData(eventLogs);

//   // Transform to devices
//   const devices: SMPSDevice[] = [];
//   deviceMap.forEach(({ eventLog, parsedData, timeDiffMs }) => {
//     devices.push(transformEventLogToDevice(eventLog, parsedData, timeDiffMs));
//   });

//   // Sort devices by station and name
//   devices.sort((a, b) => {
//     if (a.station !== b.station) {
//       return a.station.localeCompare(b.station);
//     }
//     return a.name.localeCompare(b.name);
//   });

//   // Generate alerts
//   const alerts = generateAlertsFromDevices(devices);

//   // Calculate statistics
//   const stats = calculateStatsFromDevices(devices);

//   // Calculate station statistics
//   const stationStats = calculateStationStats(devices);

//   // Extract unique stations dynamically
//   const uniqueStations = [...new Set(devices.map((d) => d.station))].sort();
//   const stations = ['ALL', ...uniqueStations];

//   return {
//     devices,
//     alerts,
//     stats,
//     stationStats,
//     stations,
//     totalElements,
//     totalPages,
//     currentPage,
//   };
// };
